import React from "react";
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LandingNav from "../components/landing/sections/LandingNav";
import LandingFooter from "../components/landing/sections/LandingFooter";

export interface LegalSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface LegalDocumentLayoutProps {
  title: string;
  lastUpdated: string;
  intro: React.ReactNode;
  sections: LegalSection[];
  relatedLink: {
    label: string;
    to: string;
  };
}

const sectionSx = {
  scrollMarginTop: 96,
  "& p": {
    mb: 2,
  },
  "& ul": {
    mt: 0,
    mb: 2,
    pl: 3,
  },
  "& li": {
    mb: 1,
    lineHeight: 1.65,
  },
};

const LegalDocumentLayout: React.FC<LegalDocumentLayoutProps> = ({
  title,
  lastUpdated,
  intro,
  sections,
  relatedLink,
}) => {
  // Disable page-level overscroll (rubber-band bounce + scroll-chaining) while a
  // legal page is mounted. The document root (html) is the scroll container, so set
  // it there; restore the prior value on unmount so other routes are unaffected.
  // Mirrors RetroLanding's effect.
  React.useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overscrollBehavior;
    html.style.overscrollBehavior = "none";
    return () => {
      html.style.overscrollBehavior = prev;
    };
  }, []);

  return (
    <>
    <div className="retroui-root">
      <LandingNav />
    </div>
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: "primary.main",
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 1.5,
            }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {lastUpdated}
          </Typography>
          <Box sx={{ maxWidth: 760 }}>{intro}</Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="nav"
          aria-label={`${title} sections`}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            borderRadius: 1,
            p: { xs: 2, sm: 3 },
            mb: 5,
          }}
        >
          <Typography variant="h2" component="h2" sx={{ fontSize: "1rem", mb: 2 }}>
            On this page
          </Typography>
          <Stack component="ol" spacing={1} sx={{ m: 0, pl: 3 }}>
            {sections.map((section) => (
              <Typography component="li" variant="body2" key={section.id}>
                <Link href={`#${section.id}`} underline="hover">
                  {section.title}
                </Link>
              </Typography>
            ))}
          </Stack>
        </Box>

        <Stack spacing={5}>
          {sections.map((section, index) => (
            <Box component="section" id={section.id} key={section.id} sx={sectionSx}>
              {index > 0 && <Divider sx={{ mb: 5 }} />}
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  color: "text.primary",
                  fontSize: { xs: "1.35rem", md: "1.6rem" },
                  mb: 2,
                }}
              >
                {index + 1}. {section.title}
              </Typography>
              {section.content}
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 5 }} />
        <Typography variant="body2" color="text.secondary">
          Related document:{" "}
          <Link component={RouterLink} to={relatedLink.to} underline="hover">
            {relatedLink.label}
          </Link>
        </Typography>
      </Container>
    </Box>
    <div className="retroui-root">
      <LandingFooter />
    </div>
    </>
  );
};

export default LegalDocumentLayout;
